import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";
import formatDate from "./formatDate";

export default function LogbookProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(String(proposal.summary?.entry_value ?? ""));

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const summary = proposal.summary || {};

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: action,
      };
      // Pass edited value as override
      if (action === "confirm" && value !== String(summary.entry_value ?? "")) {
        payload.overrides = { value: value.trim() };
      }
      const res = await postRightworkAPI(API_ops_assistant_confirm_action, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoading(false);
  };

  // Confirmed state
  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Logbook entry created</div>
          {summary.type && (
            <div className="text-xs opacity-80">{summary.type}</div>
          )}
        </div>
      </div>
    );
  }

  // Cancelled state
  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Logbook entry cancelled</span>
      </div>
    );
  }

  // Pending state — editable entry
  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <MenuBookIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {summary.title || "Create Logbook Entry"}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2 space-y-2">
        {/* Category and date */}
        <div className="flex items-center gap-3 text-sm">
          <div>
            <span className="text-gray-500">Category: </span>
            <span className="font-medium text-gray-700">
              {summary.type || ""}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Date: </span>
            <span className="font-medium text-gray-700">
              {formatDate(summary.dates) || "today"}
            </span>
          </div>
        </div>

        {/* Editable value */}
        <div>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 resize-none"
            rows={Math.min(Math.max(value.split("\n").length, 4), 10)}
            placeholder="Entry value..."
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={loading || !value.trim()}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Save Entry
        </button>
      </div>
    </div>
  );
}
